import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Mail, Phone, MapPin } from "lucide-react";
import { Avatar } from "@/components/Avatar";

// Coloured role chip lookup — kept in sync with the rest of the app.
const ROLE_META = {
  admin: { label: "Administrator", cls: "bg-brand-tint text-brand-dark border-brand/40" },
  business_development: { label: "Business Development", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  accounts: { label: "Accounts", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

const ROLE_ORDER = ["admin", "business_development", "accounts"];

export default function Team() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/team/directory")
      .then(({ data }) => setMembers(data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return members;
    return members.filter((m) => {
      return [m.name, m.role, ROLE_META[m.role]?.label, m.country, m.email, m.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [q, members]);

  const groups = useMemo(() => {
    const g = {};
    for (const m of filtered) {
      const r = m.role || "other";
      (g[r] = g[r] || []).push(m);
    }
    return g;
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight" data-testid="team-heading">
            Team Directory
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Everyone who has an active account at Tecticonism Studio.
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, role, country, email"
            className="pl-9"
            data-testid="team-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading team…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center border-slate-200 shadow-none text-slate-500">
          No teammates match your search.
        </Card>
      ) : (
        ROLE_ORDER.filter((r) => groups[r]?.length).map((role) => (
          <div key={role} className="space-y-3" data-testid={`team-group-${role}`}>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-heading font-semibold uppercase tracking-wider text-slate-500">
                {ROLE_META[role]?.label || role}
              </h2>
              <span className="text-xs text-slate-400">· {groups[role].length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups[role].map((m) => (
                <Card key={m.id} className="border-slate-200 shadow-none p-4" data-testid={`team-card-${m.id}`}>
                  <div className="flex items-start gap-3">
                    <Avatar src={m.avatar_url} name={m.name} size="w-12 h-12" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-heading font-semibold text-slate-900 truncate">
                          {m.name || "Unnamed"}
                        </div>
                      </div>
                      <span className={`mt-0.5 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${ROLE_META[m.role]?.cls || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {ROLE_META[m.role]?.label || m.role}
                      </span>
                      {m.bio && (
                        <div className="mt-2 text-xs italic text-slate-600 border-l-2 border-brand/40 pl-2" data-testid={`team-bio-${m.id}`}>
                          "{m.bio}"
                        </div>
                      )}
                      <div className="mt-3 space-y-1 text-xs text-slate-600">
                        {m.email && (
                          <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 hover:text-brand" data-testid={`team-email-${m.id}`}>
                            <Mail className="w-3 h-3 shrink-0" /> {m.email}
                          </a>
                        )}
                        {m.phone && (
                          <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 hover:text-brand" data-testid={`team-phone-${m.id}`}>
                            <Phone className="w-3 h-3 shrink-0" /> {m.phone}
                          </a>
                        )}
                        {m.country && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3 h-3 shrink-0" /> {m.country}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
